import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import AppStoreIcon from '../../assets/images/store/appstore.png';
import PlayStoreIcon from '../../assets/images/store/playstore.png';

export function SolutionSectionView({ activeStep, steps, current }) {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsLightboxOpen(false);
      }
    };
    if (isLightboxOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLightboxOpen]);

  const calculateFillWidth = () => {
    return `${(activeStep / (steps.length - 1)) * 100}%`;
  };

  return (
    <section
      id="solution"
      className="waya-works-wrapper"
      data-active-step={activeStep}
    >
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '24px', 
        width: '100%', 
        position: 'relative', 
        zIndex: 100,
        paddingTop: '10px' 
      }}>
        <h2 className="waya-section-heading">How to Get Started</h2>
        <p className="waya-section-subline" style={{ margin: '0 auto' }}>
          Your journey with WAYABANK starts here! Just follow these easy steps
        </p>
      </div>

      <div className="waya-works-split">
        {/* Left Column: Phone  */}
        <div className="waya-works-mockup-viewer" style={{ flexDirection: 'column', gap: '8px' }}>
          <img
            key={`mockup-${activeStep}`}
            src={current}
            alt={`Step ${activeStep + 1} mockup`}
            className="waya-works-phone"
            onClick={() => setIsLightboxOpen(true)}
            style={{
              animation: 'wayaFadeIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
              cursor: 'zoom-in',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.04)';
              e.currentTarget.style.filter = 'drop-shadow(0 30px 80px rgba(255, 68, 0, 0.35))';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.filter = 'drop-shadow(0 30px 80px rgba(0, 0, 0, 0.25))';
            }}
          />
          <span 
            className="waya-click-to-view"
            onClick={() => setIsLightboxOpen(true)}
            style={{ 
              fontSize: '12px', 
              color: '#667085', 
              fontWeight: '500', 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '6px',
              cursor: 'pointer',
              opacity: 0.8,
              transition: 'all 0.2s ease',
              marginTop: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#ff4400';
              e.currentTarget.style.opacity = 1;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#667085';
              e.currentTarget.style.opacity = 0.8;
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }}><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line><line x1="11" y1="8" x2="11" y2="14"></line><line x1="8" y1="11" x2="14" y2="11"></line></svg>
            Click to view
          </span>
        </div>

        {/* Right Column: Active Step Details */}
        <div className="waya-works-info-viewer">
          <div
            key={`info-${activeStep}`}
            style={{ animation: 'wayaSlideUp 0.6s ease-out' }}
          >
            <span
              className="waya-badge"
              style={{
                background: 'var(--waya-primary-light)',
                color: 'var(--waya-primary)',
              }}
            >
              Step {activeStep + 1}
            </span>
            <h3 className="waya-works-active-title">
              {steps[activeStep].title}
            </h3>
            <p className="waya-works-active-desc">
              {steps[activeStep].description}
            </p>

            {/* In-step download buttons for Step 2 */}
            {activeStep === 1 && (
              <div className="d-flex gap-3 mt-4 flex-wrap">
                <a href="https://apps.apple.com/us/app/wayabank/id1658552439" className="waya-store-btn" target="_blank" rel="noreferrer" style={{ background: '#000', color: '#fff', border: 'none', padding: '10px 16px' }}>
                  <img src={AppStoreIcon} alt="App Store" style={{ height: '16px' }} />
                  <div className="d-flex flex-column align-items-start">
                    <span style={{ fontSize: '8px', opacity: '0.6', lineHeight: 1 }}>Download on the</span>
                    <span style={{ fontSize: '12px', fontWeight: '700', lineHeight: 1 }}>App Store</span>
                  </div>
                </a>
                <a href="https://play.google.com/store/apps/details?id=ng.wayabank" className="waya-store-btn" target="_blank" rel="noreferrer" style={{ background: '#000', color: '#fff', border: 'none', padding: '10px 16px' }}>
                  <img src={PlayStoreIcon} alt="Google Play" style={{ height: '16px' }} />
                  <div className="d-flex flex-column align-items-start">
                    <span style={{ fontSize: '8px', opacity: '0.6', lineHeight: 1 }}>GET IT ON</span>
                    <span style={{ fontSize: '12px', fontWeight: '700', lineHeight: 1 }}>Google Play</span>
                  </div>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress Indicator at the bottom */}
      <div className="waya-works-progress-container">
        <div className="waya-works-line">
          <div
            className="waya-works-line-fill"
            style={{ width: calculateFillWidth() }}
          />
        </div>

        <div className="waya-works-steps">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`waya-works-step ${index <= activeStep ? 'active' : ''}`}
            >
              <div className="waya-works-circle">{index + 1}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Download Buttons below the steps */}
      <div className="d-flex justify-content-center gap-3 my-4 flex-wrap" style={{ position: 'relative', zIndex: 10 }}>
        <a href="https://apps.apple.com/us/app/wayabank/id1658552439" className="waya-store-btn" target="_blank" rel="noreferrer" style={{ background: '#000', color: '#fff', border: 'none' }}>
          <img src={AppStoreIcon} alt="App Store" style={{ height: '20px' }} />
          <div className="d-flex flex-column align-items-start">
            <span style={{ fontSize: '10px', opacity: '0.6', lineHeight: 1 }}>Download on the</span>
            <span style={{ fontSize: '14px', fontWeight: '700', lineHeight: 1 }}>App Store</span>
          </div>
        </a>
        <a href="https://play.google.com/store/apps/details?id=ng.wayabank" className="waya-store-btn" target="_blank" rel="noreferrer" style={{ background: '#000', color: '#fff', border: 'none' }}>
          <img src={PlayStoreIcon} alt="Google Play" style={{ height: '20px' }} />
          <div className="d-flex flex-column align-items-start">
            <span style={{ fontSize: '10px', opacity: '0.6', lineHeight: 1 }}>GET IT ON</span>
            <span style={{ fontSize: '14px', fontWeight: '700', lineHeight: 1 }}>Google Play</span>
          </div>
        </a>
      </div>

      {/* Lightbox Overlay */}
      {isLightboxOpen && createPortal(
        <div 
          className="waya-lightbox-overlay" 
          onClick={() => setIsLightboxOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(16, 24, 40, 0.85)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'wayaFadeIn 0.3s ease-out',
            cursor: 'zoom-out'
          }}
        >
          {/* Translucent Close Button */}
          <button 
            onClick={() => setIsLightboxOpen(false)}
            style={{
              position: 'absolute',
              top: '24px',
              right: '24px',
              background: 'rgba(255, 255, 255, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              color: '#fff',
              borderRadius: '50%',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '24px',
              transition: 'all 0.25s ease',
              outline: 'none',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            ✕
          </button>

          {/* Enlarged Phone Image Box */}
          <div 
            onClick={(e) => e.stopPropagation()} 
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative'
            }}
          >
            <img
              src={current}
              alt={`Step ${activeStep + 1} mockup enlarged`}
              style={{
                maxWidth: '100%',
                maxHeight: '85vh',
                width: 'auto',
                height: 'auto',
                borderRadius: '36px',
                border: '10px solid #000',
                boxShadow: '0 25px 60px rgba(0, 0, 0, 0.55)',
                animation: 'wayaZoomIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
              }}
            />
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}
